// src/pages/GalleryPage.jsx
import React, { useState } from "react";
import { Btn, Modal, SectionHeader, EmptyState, InfoBanner, Input, FileUpload, ProgressBar, Tabs, Badge } from "../components/UI";
import { uploadFile, deleteFile } from "../utils/uploadFile";
import { addDocument, deleteDocument } from "../hooks/useFirestore";

function GalleryUploadForm({ onSave, onClose }) {
  const [tab, setTab] = useState("image");
  const [caption, setCaption] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const handleSave = async () => {
    const file = tab === "image" ? imageFile : videoFile;
    if (!file) { setError(`Please select a ${tab} file.`); return; }
    setError(""); setUploading(true);

    try {
      const path = tab === "image" ? "gallery/images" : "gallery/videos";
      const fileURL = await uploadFile(file, path, p => setProgress(p));
      setProgress(100);
      await addDocument("gallery", { caption, fileURL, fileType: tab });
      onSave();
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    } finally { setUploading(false); }
  };

  return (
    <div>
      {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
        padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c53030" }}>{error}</div>}
      {uploading && <ProgressBar value={progress} label="Uploading..." />}

      <Tabs
        tabs={[
          { value: "image", label: "Image", icon: "🖼️" },
          { value: "video", label: "Video", icon: "🎥" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "image" ? (
        <FileUpload
          label="Upload Image" accept="image/*" required
          onFile={f => { setImageFile(f); setImagePreview(URL.createObjectURL(f)); }}
          preview={imagePreview}
          hint="JPG, PNG, WebP, GIF supported"
        />
      ) : (
        <FileUpload
          label="Upload Video" accept="video/*" required
          onFile={f => setVideoFile(f)}
          hint={videoFile ? `✓ ${videoFile.name}` : "MP4, MOV, AVI — stored in Firebase Storage"}
        />
      )}

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Caption</label>
        <input
          value={caption} onChange={e => setCaption(e.target.value)}
          placeholder="Add a caption..."
          style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 8,
            fontFamily: "inherit", fontSize: 14, background: "#fafaf8", boxSizing: "border-box" }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 14, borderTop: "1px solid #eee" }}>
        <Btn onClick={onClose} variant="secondary" disabled={uploading}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={uploading}>⬆ Upload</Btn>
      </div>
    </div>
  );
}

export default function GalleryPage({ gallery, isAdmin }) {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all");

  const filtered = filter === "all" ? gallery : gallery.filter(g => g.fileType === filter);

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      if (item.fileURL) await deleteFile(item.fileURL);
      await deleteDocument("gallery", item.id);
    } catch (err) { alert("Delete failed."); }
  };

  return (
    <div>
      {!isAdmin && <InfoBanner type="warning">👁 View-only mode.</InfoBanner>}

      <SectionHeader
        title="Gallery"
        action={isAdmin && <Btn onClick={() => setShowForm(true)}>+ Add Media</Btn>}
      />

      {/* Filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        {["all", "image", "video"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "5px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600,
            border: `1px solid ${filter === f ? "#2d7a3a" : "#ddd"}`,
            background: filter === f ? "#e8f5e9" : "#fff",
            color: filter === f ? "#2d7a3a" : "#666",
            cursor: "pointer", fontFamily: "inherit", textTransform: "capitalize",
          }}>
            {f === "all" ? `All (${gallery.length})` :
             f === "image" ? `🖼 Images (${gallery.filter(g => g.fileType === "image").length})` :
             `🎥 Videos (${gallery.filter(g => g.fileType === "video").length})`}
          </button>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 12 }}>
        {filtered.map(item => (
          <div key={item.id} style={{ background: "#fff", border: "1px solid #e8f0e9", borderRadius: 10, overflow: "hidden" }}>
            {/* Preview */}
            <div style={{ height: 120, background: item.fileType === "video" ? "#1a1a2e" : "#f0f0f0",
              display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
              {item.fileType === "image" && item.fileURL ? (
                <img src={item.fileURL} alt={item.caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : item.fileType === "video" && item.fileURL ? (
                <video src={item.fileURL} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
              ) : (
                <span style={{ fontSize: 32 }}>{item.fileType === "video" ? "🎥" : "🖼️"}</span>
              )}
            </div>
            {/* Meta */}
            <div style={{ padding: "8px 10px" }}>
              <div style={{ fontSize: 12, color: "#555", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.caption || <span style={{ color: "#bbb" }}>No caption</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Badge color={item.fileType === "video" ? "#2b6cb0" : "#2d7a3a"}>{item.fileType}</Badge>
                {isAdmin && (
                  <button onClick={() => handleDelete(item)}
                    style={{ background: "none", border: "none", color: "#e53e3e", cursor: "pointer", fontSize: 16, padding: 0 }}>
                    🗑
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!filtered.length && (
          <div style={{ gridColumn: "1 / -1" }}>
            <EmptyState icon="🖼️" message={isAdmin ? "No gallery items yet. Click '+ Add Media' to upload." : "No gallery items available."} />
          </div>
        )}
      </div>

      {showForm && (
        <Modal title="Add to Gallery" onClose={() => setShowForm(false)}>
          <GalleryUploadForm onSave={() => setShowForm(false)} onClose={() => setShowForm(false)} />
        </Modal>
      )}
    </div>
  );
}
